import { AudioPlayer } from '@components/AudioPlayer';
import { FileDownloadButton } from '@components/FileDownloadButton';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { buildJobMaterialFileName } from '@helpers/buildJobMaterialFileName';
import { handleDownloadError } from '@helpers/jobLinkHelpers';
import { downloadFromUrl } from '@helpers/downloadFromUrl';
import { downloadViaBrowser } from '@helpers/downloadViaBrowser';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQueries } from '@tanstack/react-query';
import { JobAttachmentResponseDto, JobDtoAttachment, NoticeExtractorService, PrismaMediaDto } from 'app/API';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { createFetchJobAttachmentQueryFn } from '../../../../../../api/fetchJobAttachment';
import { createFetchSignedMediaUrlQueryFn } from '../../../../../../api/fetchSignedMediaUrl';
import { fetchSignedMediaDownloadUrl } from '../../../../../../api/fetchSignedMediaDownloadUrl';
import { usePageContext } from '../../../../../../providers/PageProvider';
import { Divider } from '../../styles/Divider';
import { InfoBlockContent } from '../../styles/InfoBlockContent';
import { InfoBlockFullSize } from '../../styles/InfoBlockFullSize';
import { InfoBlockTitle } from '../../styles/InfoBlockTitle';
import { JobSection } from '../../styles/JobSection';
import { JobSectionLeft } from '../../styles/JobSectionLeft';
import { JobSectionRight } from '../../styles/JobSectionRight';
import { AudioWithQualityScore } from './components/AudioWithQualityScore';
import MagicWandIcon from '@components/icons/MagicWand.svg';
import DiagonalArrowIcon from '@components/icons/DiagonalArrow.svg';
import LoadingIcon from '@components/icons/Loading.svg';
import { IconButtonRegular } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { getNoticeExtractorErrorMessage } from '@pages/NoticeExtractor/helpers/getNoticeExtractorErrorMessage';
import { routes } from '@routes';

type TJobAttachmentWithNoticeData = JobDtoAttachment & {
  id?: string;
  attachmentType?: 'REPORTER_NOTES' | 'GENERAL' | 'NOTICE';
  extracted?: boolean;
};

const isPdfAttachment = (attachment: TJobAttachmentWithNoticeData) => {
  return /\.pdf$/i.test(attachment.fileName || attachment.path);
};

const isNoticePdfAttachment = (attachment: TJobAttachmentWithNoticeData) => {
  return attachment.attachmentType === 'NOTICE' && isPdfAttachment(attachment) && !!attachment.id;
};

const NOTICE_POLL_INTERVAL_MS = 5000;

export const MaterialsSection = () => {
  const { call } = useAPI();
  const navigate = useNavigate();
  const {
    job,
    showMaterials,
    showDownloads,
    showBackupFiles,
    showExhibitFiles,
    showReporterNotes,
    showSourceFiles,
    setJob,
  } = usePageContext();
  const [startingNoticeExtractionIds, setStartingNoticeExtractionIds] = useState<Record<string, boolean>>({});
  const [runningNoticeExtractionIds, setRunningNoticeExtractionIds] = useState<Record<string, boolean>>({});
  const [noticeAttachmentExtractedById, setNoticeAttachmentExtractedById] = useState<Record<string, boolean>>({});

  const updateJobAttachment = (attachment: JobAttachmentResponseDto) => {
    const nextAttachment: JobDtoAttachment = {
      id: attachment.id,
      fileName: attachment.filename,
      path: attachment.filename,
      fileSize: attachment.fileSize,
      extracted: attachment.extracted,
      attachmentType: attachment.attachmentType,
    };

    setJob((currentJob) => {
      if (!currentJob) {
        return currentJob;
      }

      return {
        ...currentJob,
        attachments: currentJob.attachments.map((item) =>
          item.id === attachment.id || item.path === attachment.filename || item.fileName === attachment.filename
            ? { ...item, ...nextAttachment }
            : item,
        ),
      };
    });
  };

  const downloadSourceAudioMutation = useMutation({
    mutationFn: async ({ jobId, fileName }: { jobId: string; fileName: string }) => {
      const signed = await fetchSignedMediaDownloadUrl(call, { jobId, file: fileName });
      if (!signed?.signedUrl) {
        throw new Error('No signed URL');
      }
      return signed.signedUrl;
    },
    onSuccess: async (signedUrl, { fileName }) => {
      await downloadFromUrl(signedUrl, buildJobMaterialFileName(job?.name || 'job', fileName));
    },
  });

  const downloadSourceAudio = (jobId: string, fileName: string) =>
    downloadSourceAudioMutation.mutateAsync({ jobId, fileName }).then(() => undefined);

  const downloadFile = (jobId: string, fileName: string) =>
    downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/transcript/${encodeURIComponent(fileName)}`,
      buildJobMaterialFileName(job?.name || 'job', fileName),
    );

  const downloadExhibitFile = (jobId: string, fileName: string) =>
    downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/exhibits/${encodeURIComponent(fileName)}`,
      buildJobMaterialFileName(job?.name || 'job', fileName),
    );

  const downloadReporterNote = (jobId: string, fileName: string) =>
    downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/reporter-notes/${encodeURIComponent(fileName)}`,
      buildJobMaterialFileName(job?.name || 'job', fileName),
    );

  const downloadAttachment = (jobId: string, fullPath: string, fileName?: string) =>
    downloadFromUrl(
      `${window.location.origin}/api/media/${jobId}/attachments/${encodeURIComponent(fullPath)}`,
      buildJobMaterialFileName(job?.name || 'job', fileName ?? fullPath),
    );

  const startNoticeExtraction = async (attachment: TJobAttachmentWithNoticeData) => {
    if (!job?.id || !attachment.id) {
      return;
    }

    setStartingNoticeExtractionIds((prev) => ({ ...prev, [attachment.id as string]: true }));
    try {
      const generateKeyterms = window.confirm('Run keyterms extraction for this notice?');
      await NoticeExtractorService.extractJobAttachment({
        jobId: job.id,
        attachmentId: attachment.id,
        requestBody: { generateKeyterms },
      });
      setRunningNoticeExtractionIds((prev) => ({ ...prev, [attachment.id as string]: true }));
      toast.success('Notice extraction started');
    } catch (error) {
      toast.error(getNoticeExtractorErrorMessage(error));
    } finally {
      setStartingNoticeExtractionIds((prev) => ({ ...prev, [attachment.id as string]: false }));
    }
  };

  const openNoticeExtraction = (attachmentId: string) => {
    navigate(`${routes.adminNoticeExtractor.make()}?workflowId=${encodeURIComponent(attachmentId)}`);
  };

  const filesToFetch = useMemo(() => {
    if (!job?.id) {
      return [];
    }
    const files = new Set<string>();
    if (job.media?.length) {
      job.media.forEach((media: PrismaMediaDto) => {
        if (media.fileName) {
          files.add(media.fileName);
        }
      });
    } else if (job.sourceFiles?.length) {
      job.sourceFiles.forEach((file) => files.add(file));
    }
    return [...files];
  }, [job?.id, job?.media, job?.sourceFiles]);

  const signedUrlQueries = useQueries({
    queries: filesToFetch.map((file) => ({
      queryKey: adminJobsKeys.signedMediaUrl(job?.id ?? '', file),
      queryFn: createFetchSignedMediaUrlQueryFn(call),
      enabled: !!job?.id,
    })),
  });

  const signedUrls: Record<string, string | undefined> = {};
  filesToFetch.forEach((file, index) => {
    const query = signedUrlQueries[index];
    signedUrls[file] = query?.isError ? '' : query?.data ? query.data.signedUrl ?? '' : undefined;
  });

  const noticeAttachmentsToPoll = useMemo(
    () =>
      ((job?.attachments ?? []) as TJobAttachmentWithNoticeData[]).filter((attachment) => {
        if (!isNoticePdfAttachment(attachment)) {
          return false;
        }

        return !attachment.extracted;
      }),
    [job?.attachments],
  );

  const noticeAttachmentQueries = useQueries({
    queries: noticeAttachmentsToPoll.map((attachment) => ({
      queryKey: adminJobsKeys.attachment(job?.id ?? '', attachment.id as string),
      queryFn: createFetchJobAttachmentQueryFn(call),
      enabled: !!job?.id,
      refetchInterval: NOTICE_POLL_INTERVAL_MS,
    })),
  });
  const noticeAttachmentsUpdatedAt = noticeAttachmentQueries.map((query) => query.dataUpdatedAt).join(',');

  useEffect(() => {
    noticeAttachmentQueries.forEach((query) => {
      const response = query.data;
      if (!response) {
        return;
      }
      updateJobAttachment(response);
      setNoticeAttachmentExtractedById((prev) => {
        if (prev[response.id] === response.extracted) {
          return prev;
        }

        return {
          ...prev,
          [response.id]: response.extracted,
        };
      });
      if (response.extracted) {
        setRunningNoticeExtractionIds((prev) => (prev[response.id] ? { ...prev, [response.id]: false } : prev));
      }
    });
  }, [noticeAttachmentsUpdatedAt]);

  if (!job || !showMaterials) {
    return null;
  }

  return (
    <>
      <JobSection>
        <JobSectionLeft>Materials</JobSectionLeft>
        <JobSectionRight className="flex flex-col gap-6">
          {showDownloads ? (
            <InfoBlockFullSize>
              <InfoBlockTitle>Speech-to-Text Transcript</InfoBlockTitle>
              <InfoBlockContent>
                <div className="file-grid">
                  {job.results?.map((file: string, index: number) => (
                    <FileDownloadButton
                      testId={`jobDownloadItem${job.name}`}
                      key={file + index}
                      label={file}
                      onDownload={() => downloadFile(job.id, file)}
                      onError={handleDownloadError}
                      className="downloadLink"
                    />
                  ))}
                </div>
              </InfoBlockContent>
            </InfoBlockFullSize>
          ) : null}

          {showReporterNotes ? (
            <InfoBlockFullSize>
              <InfoBlockTitle>Reporter Notes</InfoBlockTitle>
              <InfoBlockContent>
                <div className="file-grid">
                  {job.reporterNotes?.map((file: string) => (
                    <FileDownloadButton
                      testId={`jobReporterNotes${job.name}`}
                      key={file}
                      label={file}
                      onDownload={() => downloadReporterNote(job.id, file)}
                      onError={handleDownloadError}
                      className="downloadLink"
                    />
                  ))}
                </div>
              </InfoBlockContent>
            </InfoBlockFullSize>
          ) : null}

          {showExhibitFiles ? (
            <InfoBlockFullSize>
              <InfoBlockTitle>Exhibit Files</InfoBlockTitle>
              <InfoBlockContent>
                <div className="file-grid">
                  {job.exhibitFiles?.map((file: string) => (
                    <FileDownloadButton
                      testId={`jobDownloadItem${job.name}`}
                      key={file}
                      label={file}
                      onDownload={() => downloadExhibitFile(job.id, file)}
                      onError={handleDownloadError}
                      className="downloadLink"
                    />
                  ))}
                </div>
              </InfoBlockContent>
            </InfoBlockFullSize>
          ) : null}

          {showBackupFiles ? (
            <InfoBlockFullSize>
              <InfoBlockTitle>Backup Files</InfoBlockTitle>
              <InfoBlockContent>
                <div className="file-grid">
                  {job.backupFiles?.map((file: string) => (
                    <AudioPlayer
                      key={file}
                      src={`${window.location.origin}/api/media/${job.id}/backup/${file}`}
                      downloadFileName={buildJobMaterialFileName(job.name, file)}
                    />
                  ))}
                </div>
              </InfoBlockContent>
            </InfoBlockFullSize>
          ) : null}

          {showSourceFiles ? (
            <InfoBlockFullSize>
              <InfoBlockTitle>Source Audio Files</InfoBlockTitle>
              <InfoBlockContent>
                {job.media?.length
                  ? job.media.map((media: PrismaMediaDto) => (
                      <AudioWithQualityScore
                        key={media.id}
                        media={media}
                        signedUrl={signedUrls[media.fileName]}
                        jobName={job.name}
                      />
                    ))
                  : job.sourceFiles?.map((file: string) => {
                      const signedUrl = signedUrls[file];
                      return (
                        <div key={file} className="mb-2">
                          {signedUrl === '' ? (
                            <span>Audio unavailable</span>
                          ) : signedUrl ? (
                            <AudioPlayer
                              src={signedUrl}
                              downloadFileName={buildJobMaterialFileName(job.name, file)}
                              onDownload={() => downloadSourceAudio(job.id, file)}
                            />
                          ) : (
                            <LinearProgressLoader active />
                          )}
                        </div>
                      );
                    })}
              </InfoBlockContent>
            </InfoBlockFullSize>
          ) : null}

          {!!job.attachments?.length && (
            <InfoBlockFullSize>
              <InfoBlockTitle className="flex items-center">
                Attachments
                <FileDownloadButton
                  label="Download attachments ZIP"
                  showLabel={false}
                  onDownload={() =>
                    downloadViaBrowser(
                      `${window.location.origin}/api/jobs/${job.id}/attachments/download-zip`,
                      'attachments.zip',
                    )
                  }
                  onError={handleDownloadError}
                  className="ml-2"
                />
              </InfoBlockTitle>
              <InfoBlockContent>
                <div className="file-grid">
                  {job.attachments?.map((file: TJobAttachmentWithNoticeData) => (
                    <div key={file.path} className="file-grid__item">
                      <FileDownloadButton
                        testId={`jobDownloadItem${job.name}`}
                        label={file.fileName}
                        onDownload={() => downloadAttachment(job.id, file.path, file.fileName)}
                        onError={handleDownloadError}
                        className="downloadLink"
                      />
                      {isNoticePdfAttachment(file) ? (
                        <>
                          {noticeAttachmentExtractedById[file.id as string] ?? file.extracted ? (
                            <IconButtonRegular
                              aria-label={`Open notice extraction for ${file.fileName}`}
                              title="Open notice extraction"
                              onClick={() => openNoticeExtraction(file.id as string)}
                            >
                              <DiagonalArrowIcon className="w-4 h-4" />
                            </IconButtonRegular>
                          ) : null}
                          {!(noticeAttachmentExtractedById[file.id as string] ?? file.extracted) ? (
                            <IconButtonRegular
                              aria-label={`Start notice extraction for ${file.fileName}`}
                              title="Start notice extraction"
                              disabled={!!startingNoticeExtractionIds[file.id as string]}
                              onClick={() => startNoticeExtraction(file)}
                            >
                              {startingNoticeExtractionIds[file.id as string] ||
                              runningNoticeExtractionIds[file.id as string] ? (
                                <LoadingIcon className="h-4 w-4 animate-spin" />
                              ) : (
                                <MagicWandIcon className="w-4 h-4" />
                              )}
                            </IconButtonRegular>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
              </InfoBlockContent>
            </InfoBlockFullSize>
          )}

          {job.notes ? (
            <InfoBlockFullSize>
              <InfoBlockTitle>Notes</InfoBlockTitle>
              <InfoBlockContent>{job.notes}</InfoBlockContent>
            </InfoBlockFullSize>
          ) : null}
        </JobSectionRight>
      </JobSection>
      <Divider />
    </>
  );
};
